<script setup>
import { computed } from 'vue'
import { formatMoney, currencyLines, refLabel } from '@/Utils/siteTransactions'

// Six summary cards over a castr transactions `Summary` (§6e). The same
// component sits on the Overview tab (compact) and on top of the Transactions
// tab. `summary` is null while loading or when castr cannot answer yet.
const props = defineProps({
	summary: {
		type: Object,
		default: null,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	// Runtime content / categories to turn `item:12` into a title.
	content: {
		type: Array,
		default: () => [],
	},
	categories: {
		type: Array,
		default: () => [],
	},
	compact: {
		type: Boolean,
		default: false,
	},
})

const lines = computed(() => currencyLines(props.summary))
const mixed = computed(() => props.summary?.currency === 'mixed')
const money = (key) => {
	if (!props.summary) return '—'
	if (!mixed.value) return formatMoney(props.summary[key], props.summary.currency)
	return lines.value.map((l) => formatMoney(l[key], l.currency)).join(' · ')
}
const topPlan = computed(() => props.summary?.top_products?.[0] || null)
const topContent = computed(() => props.summary?.top_content?.[0] || null)
const count = computed(() => props.summary?.count ?? null)
const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`

const cards = computed(() => [
	{ id: 'gross', label: 'Gross revenue', value: money('gross'), sub: props.summary?.free_count ? `${plural(props.summary.free_count, 'free grant')} not counted` : '' },
	{ id: 'refunded', label: 'Refunded', value: money('refunded'), sub: props.summary?.refund_count ? plural(props.summary.refund_count, 'refund') : '' },
	{ id: 'net', label: 'Net', value: money('net'), sub: mixed.value ? 'Several currencies — totals shown per currency' : '' },
	{ id: 'count', label: 'Transactions', value: count.value === null ? '—' : String(count.value), sub: props.summary?.pending_count ? `${plural(props.summary.pending_count, 'unfinished checkout')} not counted` : '' },
	{ id: 'top-plan', label: 'Most purchased plan', value: topPlan.value ? topPlan.value.title || topPlan.value.public_id : '—', sub: topPlan.value ? `${plural(topPlan.value.count, 'purchase')}${topPlan.value.gross !== null && topPlan.value.gross !== undefined ? ` · ${formatMoney(topPlan.value.gross, props.summary.currency === 'mixed' ? undefined : props.summary.currency)}` : ''}` : '' },
	{ id: 'top-content', label: 'Most purchased content', value: topContent.value ? topContent.value.title || refLabel(topContent.value.item_ref, { content: props.content, categories: props.categories }) : '—', sub: topContent.value ? `${plural(topContent.value.count, 'purchase')} of plans including it` : '' },
])
</script>

<template>
	<div class="grid gap-3" :class="compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6'" data-testid="revenue-summary" :aria-busy="loading">
		<div v-for="card in cards" :key="card.id" class="min-w-0 rounded-lg bg-surface-3 px-3 py-2.5" :data-testid="`revenue-${card.id}`">
			<p class="mb-0.5 truncate text-tiny text-surface-7">{{ card.label }}</p>
			<p class="mb-0 truncate font-semibold text-white" :class="compact ? 'text-sm' : 'text-base'" :title="card.value">
				<span v-if="loading && !summary" class="inline-block h-4 w-16 animate-pulse rounded bg-surface-4 align-middle" />
				<template v-else>{{ card.value }}</template>
			</p>
			<p v-if="card.sub && !compact" class="mb-0 truncate text-[11px] text-surface-7" :title="card.sub">{{ card.sub }}</p>
		</div>
	</div>
</template>
